import * as React from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import type { ThemeOptions } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

interface AppThemeProps {
  children: React.ReactNode;
  disableCustomTheme?: boolean;
  themeComponents?: ThemeOptions["components"];
}

export default function AppTheme(props: AppThemeProps) {
  const { children, disableCustomTheme, themeComponents } = props;
  const theme = React.useMemo(() => {
    return disableCustomTheme
      ? {}
      : createTheme({
          typography: {
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          },
          cssVariables: {
            colorSchemeSelector: "data-mui-color-scheme",
            cssVarPrefix: "template",
          },
          components: {
            ...themeComponents,
            MuiCssBaseline: {
              styleOverrides: `
                * {
                  -webkit-tap-highlight-color: transparent;
                }
                
                .highlight {
                  animation: blink 1s ease-in-out;
                  outline: 2px solid #1976d2;
                  border-radius: 4px;
                }
          
                @keyframes blink {
                  0% { background-color: #e3f2fd; }
                  50% { background-color: #bbdefb; }
                  100% { background-color: #e3f2fd; }
                }
                @keyframes blinkBox {
                    0% {
                      box-shadow: 0 0 0 0 rgba(120, 144, 156, 0.2);
                    }
                    50% {
                      box-shadow: 0 0 7px 2px rgba(120, 144, 156, 0.5);
                    }
                    100% {
                      box-shadow: 0 0 0 0 rgba(33, 150, 243, 0);
                    }
                  }
              `,
            },
            MuiPaper: {
              styleOverrides: {
                root: {
                  ".MuiAlert-action": {
                    display: "none",
                  },
                },
              },
            },
            MuiInputBase: {
              styleOverrides: {
                root: {
                  "input:-webkit-autofill": {
                    boxShadow: "none !important",
                    WebkitTextFillColor: "black !important",
                  },
                  paddingRight: "0 !important",
                  "&.Mui-disabled": {
                    svg: {
                      color: "rgba(0, 0, 0, 0.38) !important",
                      cursor: "default !important",
                    },
                  },
                },
              },
            },
            MuiTextField: {
              styleOverrides: {
                root: {
                  "&.password .MuiFormHelperText-root.Mui-error": {
                    top: "70%",
                  },
                  "&.comment textarea": {
                    height: "120px !important",
                    overflowY: "scroll !important",
                  },
                  "&.note textarea": {
                    height: "80px !important",
                    overflowY: "scroll !important",
                  },
                  "&.priceExpiration": {
                    input: {
                      fontSize: "14px",
                      lineHeight: "12px",
                    },
                  },
                  "&.editPrice": {
                    maxWidth: "90px",
                    input: {
                      fontSize: "14px",
                      lineHeight: "10px",
                    },
                  },
                  "&.editNote": {
                    maxWidth: "160px",
                    input: {
                      fontSize: "14px",
                      lineHeight: "10px",
                    },
                  },
                  "&.editColumn": {
                    width: "100%",
                    input: {
                      fontSize: "14px",
                      lineHeight: "10px",
                    },
                  },
                  "&.editRangePrice": {
                    maxWidth: "90px",
                    input: {
                      fontSize: "14px",
                      lineHeight: "12px",
                      padding: 0,
                    },
                  },
                },
              },
            },
            MuiFormLabel: {
              styleOverrides: {
                root: {
                  ".MuiFormLabel-asterisk": {
                    color: "rgba(211, 47, 47, 1)",
                  },
                },
              },
            },
            MuiFormHelperText: {
              styleOverrides: {
                root: {
                  position: "absolute",
                  bottom: "0",
                  marginTop: "0",

                  "&.Mui-error": {
                    top: "100%",
                    paddingTop: "5px",
                    lineHeight: "1.3",
                  },
                },
              },
            },
            MuiAutocomplete: {
              styleOverrides: {
                root: {
                  "&.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
                    paddingRight: "65px !important",
                  },
                  ".MuiAutocomplete-inputRoot": {
                    flexWrap: "nowrap",
                  },
                  ".MuiAutocomplete-tag": {
                    maxWidth: "calc(100% - 60px)",
                  },
                  "&.carrier-autocomplete .MuiAutocomplete-tag": {
                    maxWidth: "150px",
                  },
                  ".MuiAutocomplete-input": {
                    maxWidth: "calc(100% - 70px)",
                  },
                  "&.Mui-focused": {
                    ".MuiAutocomplete-inputRoot": {
                      flexWrap: "wrap",
                    },
                  },
                  "&.hide-clear": {
                    ".MuiAutocomplete-clearIndicator": {
                      display: "none !important",
                      visibility: "hidden !important",
                    },
                  },
                },
              },
            },
            MuiAccordion: {
              styleOverrides: {
                root: {
                  "&.selectedServices, &.scInsurance, &.scCargo, &.scEquipment":
                    {
                      ".MuiAccordionSummary-root": {
                        borderRadius: "4px",
                        padding: "8px",
                        backgroundColor: "rgba(245, 245, 245, 1)",
                        overflow: "hidden",
                      },
                      ".MuiAccordionSummary-root.empty": {
                        borderRadius: "0",
                        padding: "0",
                        backgroundColor: "transparent",
                        overflow: "hidden",
                      },
                      ".MuiAccordionSummary-content": {
                        overflow: "hidden",
                        alignItems: "center",
                      },
                      ".MuiAccordionDetails-root": {
                        backgroundColor: "rgba(250, 250, 250, 1)",
                        padding: "8px",
                      },
                    },
                  "&.selectedServices": {
                    ".MuiAccordionSummary-content.Mui-expanded": {
                      visibility: "hidden",
                    },
                    ":before": {
                      display: "none",
                    },
                  },
                  "&.scInsurance, &.scCargo, &.scEquipment, &.scBill": {
                    border: "0 transparent !important",
                    borderRadius: "4px !important",
                    ".MuiAccordionSummary-root": {
                      flexDirection: "row-reverse",
                    },
                    ".MuiAccordionDetails-root": {
                      padding: "8px 0",
                    },
                    ":before": {
                      display: "none",
                    },
                    marginTop: "8px",
                  },
                  "&.scBill": {
                    marginTop: "0",
                    "&.inbox": {
                      ".MuiAccordionSummary-root": {
                        padding: "0",
                      },
                    },
                  },
                  "&.scInsurance + .MuiAccordion-root, &.scCargo + .MuiAccordion-root, &.scEquipment + .MuiAccordion-root":
                    {
                      ":before": {
                        display: "none",
                      },
                      marginTop: "8px",
                    },
                  "&.scCargo": {
                    padding: 0,
                    marginTop: 0,
                  },
                  "&.scEquipment": {
                    ".MuiAccordionSummary-root": {
                      backgroundColor: "rgba(238, 238, 238, 1)",
                    },
                    ".MuiAccordionDetails-root": {
                      backgroundColor: "rgba(245, 245, 245, 1)",
                      padding: "0",
                    },
                  },
                  "&.createInsurance": {
                    border: "0 transparent !important",
                    borderRadius: "4px !important",
                    ".MuiAccordionSummary-root": {
                      borderRadius: "4px",
                      padding: "8px",
                      backgroundColor: "rgba(245, 245, 245, 1)",
                      flexDirection: "row",
                      overflow: "hidden",
                    },
                    ".MuiAccordionDetails-root": {
                      backgroundColor: "rgba(250, 250, 250, 1)",
                      padding: "8px",
                    },
                  },
                  "&.createInsurance + .MuiAccordion-root": {
                    ":before": {
                      display: "none",
                    },
                    marginTop: "8px",
                  },
                },
              },
            },
            MuiAccordionDetails: {
              styleOverrides: {
                root: {
                  paddingTop: "0",
                },
              },
            },
            MuiAccordionSummary: {
              styleOverrides: {
                root: {
                  minHeight: "auto",
                  "&.Mui-focusVisible": {
                    backgroundColor: "#fff",
                  },
                  "&.Mui-expanded": {
                    minHeight: "auto",
                    margin: "0",
                  },
                },
                content: {
                  margin: "0",
                  "&.Mui-expanded": {
                    minHeight: "auto",
                    margin: "0",
                  },
                },
              },
            },
            MuiTabs: {
              styleOverrides: {
                root: {
                  "&.cargo": {
                    marginTop: "-24px",
                  },
                  "&.cargo .MuiTab-root": {
                    fontSize: "13px",
                    padding: "30px 16px",
                    color: "rgba(0, 0, 0, 0.87)",
                  },
                  "&.cargo .MuiTab-root.Mui-selected": {
                    color: "#1976d2 !important",
                  },
                  ".MuiTab-icon": {
                    marginBottom: "0 !important",
                    height: "20px",
                  },
                },
              },
            },
            MuiDialog: {
              styleOverrides: {
                root: {
                  "&.deposit .MuiDialog-paper": {
                    height: "calc(100% - 64px)",
                    maxWidth: "970px",
                  },
                },
              },
            },
            MuiTable: {
              styleOverrides: {
                root: {
                  "&.readonly": {
                    ".MuiTableCell-root": {
                      padding: "8px",
                      verticalAlign: "top",
                    },
                  },
                  "&.services": {
                    ".MuiTableCell-root": {
                      padding: "8px",
                      verticalAlign: "top",
                      "&.description": {
                        background: "white",
                        position: "sticky",
                        left: "54px",
                        zIndex: "2",
                      },
                      "&.packageDescription": {
                        paddingLeft: "12px",
                        background: "white",
                        position: "sticky",
                        left: "0",
                        zIndex: "1",
                      },
                      "&.select": {
                        background: "white",
                        position: "sticky",
                        left: "0",
                        zIndex: "2",
                      },
                      "&.selectedPackage": {
                        background: "#eef7fe !important",
                      },
                    },
                    ".MuiTableRow-root": {
                      cursor: "pointer",
                      "&:hover": {
                        background: "#f5f5f5",
                        ".MuiTableCell-root": {
                          "&.description, &.packageDescription": {
                            background: "#f5f5f5",
                          },
                          "&.select": {
                            background: "#f5f5f5",
                          },
                        },
                      },
                      "&.Mui-selected": {
                        backgroundColor: "#eef7fe",
                        ".MuiTableCell-root": {
                          "&.description, &.packageDescription": {
                            background: "#eef7fe",
                          },
                          "&.select": {
                            background: "#eef7fe",
                          },
                        },
                      },
                    },
                  },
                  "&.apl": {
                    ".MuiTableCell-root": {
                      padding: "8px",
                      verticalAlign: "top",
                    },
                    ".MuiTableRow-root": {
                      "&:hover": {
                        background: "#f5f5f5",

                        ".sticky, .id, .description": {
                          background: "#f5f5f5",
                        },
                      },
                    },
                    ".description": {
                      background: "white",
                      position: "sticky",
                      left: "64px",
                      zIndex: "2",
                    },
                    ".id": {
                      background: "white",
                      position: "sticky",
                      left: "0",
                      zIndex: "2",
                      minWidth: "64px",
                    },
                    ".sticky": {
                      position: "sticky",
                      right: 0,
                      zIndex: 3,
                      background: "white",
                      borderLeft: "none",

                      button: {
                        width: "35px",
                        height: "35px",
                      },

                      "&::before": {
                        content: "''",
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: "1px",
                        backgroundColor: "#e0e0e0",
                        zIndex: 4,
                      },
                    },
                  },
                  "&.passivePrice": {
                    ".MuiInputBase-sizeSmall": {
                      fontSize: "14px",
                      marginTop: "0 !important",
                    },
                  },
                },
              },
            },
          },
        });
  }, [disableCustomTheme, themeComponents]);
  if (disableCustomTheme) {
    return <React.Fragment>{children}</React.Fragment>;
  }
  return (
    <ThemeProvider theme={theme} disableTransitionOnChange>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}
